import { createSelector } from 'reselect';
import { sendRequest, createAsyncActionTypes, logSentryError } from '@mews/framework';
import { RouteName } from '@mews-commander/enums';
import { CommanderThunkAction } from '@mews-commander/types';
import { getStringProp } from '@mews/fp';
import { GeneralArea } from '@mews-commander/data-access';
import { push } from '@mews/routing';
import { createFetchAction, createErrorAction } from '@mews-commander/utils';
import { getCurrentProfileId } from '@mews-commander/core';
import { EnterpriseIdStorageService } from '../../auth/ducks/enterpriseIdStorageService';
import { isAppFetching, isNewAppVersionFetched, appNeedsReload } from '../reducers/status';
import { getRouterBaseUrl, getServerBasePath } from '../reducers/configuration';
import { getRouterBasePathRegex } from '../../navigation';
import prefix from '../prefix';
import { setRefreshPage } from '../components/refreshPageStore';
import { clearHistory, pushHistory } from '../../../core/history';
import { displayAppLoader, hideAppLoader } from './refresh';
import { teardown } from './teardown';

const TEMPLATE_FETCH = prefix('TEMPLATE_FETCH');
const [TEMPLATE_FETCH_STARTED, TEMPLATE_FETCH_SUCCEEDED, TEMPLATE_FETCH_FAILED] =
    createAsyncActionTypes(TEMPLATE_FETCH);

export const logError =
    ({ type, message, context }: { type: string; message: string; context?: object }): CommanderThunkAction =>
    (dispatch, getState, { loggerService }) => {
        loggerService.log(type, message, context);
    };

export const forceReload = (): CommanderThunkAction => (dispatch, getState) => {
    const state = getState();
    dispatch(
        logError({
            type: 'force-reload',
            message: 'Forced reloading.',
            context: { accountId: getCurrentProfileId(), location: window.location.href },
        })
    );

    const isFetching: boolean = isAppFetching(state);
    if (isFetching) {
        dispatch(logSentryError({ error: 'Force reload executed multiple times' }));
        return;
    }

    dispatch(teardown());
    window.location.reload();
};

export const linkRedirect =
    (url: string): CommanderThunkAction =>
    (dispatch) => {
        dispatch(teardown());
        window.location.replace(url);
    };

export interface ModalCallbacks<Data> {
    onDataReceived?: (data: Data) => void;
    onClose?: () => void;
}

/** @deprecated use tanstack navigate or enterprise navigate */
export function openModal(url: string): CommanderThunkAction {
    return (dispatch, getState) => {
        const previousUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        pushHistory(previousUrl);

        const state = getState();
        const normalizedUrl = getApplicationNormalizedUrl(state, { url });
        dispatch(push(normalizedUrl));
    };
}

/** @deprecated use tanstack navigate or enterprise navigate */
export function navigateInModal(url: string): CommanderThunkAction {
    return (dispatch, getState) => {
        const state = getState();
        const normalizedUrl = getApplicationNormalizedUrl(state, { url });
        dispatch(push(normalizedUrl));
    };
}

/** @deprecated use tanstack navigate or enterprise navigate */
export function showRouteInModal(routeName: RouteName, params = {}): CommanderThunkAction {
    return (dispatch, getState, { resolveRoute }) => {
        const url = resolveRoute(routeName, params);
        return dispatch(navigateInModal(url));
    };
}

/** @deprecated use tanstack navigate or enterprise navigate */
export function navigateInBaseModal(url: string): CommanderThunkAction {
    return (dispatch, getState) => {
        clearHistory();

        const state = getState();
        const normalizedUrl = getApplicationNormalizedUrl(state, { url });

        if (isNewAppVersionFetched(state) === true || appNeedsReload(state) === true) {
            return dispatch(linkRedirect(normalizedUrl));
        }

        return dispatch(navigateInModal(normalizedUrl));
    };
}

/**
 * Triggers rerender of the current page.
 */
export function refreshPage(): CommanderThunkAction {
    return () => {
        /**
         * Trigger rerender of current page, without hard reload of the whole app.
         */
        setRefreshPage();
    };
}

export const fetchTemplate: (...args: Parameters<typeof sendRequest>) => CommanderThunkAction =
    (url, options, formData) => async (dispatch) => {
        dispatch(createFetchAction(TEMPLATE_FETCH_STARTED, { url }));

        return sendRequest(url, options, formData)
            .then((response) => {
                // We don't put response into the action, as it can be very big.
                // It's processed by the template fetcher in following then clause.
                // @ts-expect-error $TSFixMeError: Expected 2-3 arguments, but got 1.
                dispatch(createFetchAction(TEMPLATE_FETCH_SUCCEEDED));
                return response;
            })
            .catch((e) => {
                const { name, message } = e;
                const error = { name, message };
                dispatch(createErrorAction(TEMPLATE_FETCH_FAILED, error));

                throw e;
            });
    };

export const signOut =
    (): CommanderThunkAction =>
    async (dispatch, getState, { repositories }) => {
        EnterpriseIdStorageService.clear();

        dispatch(displayAppLoader());
        return repositories.generalUsers
            .signOut({ application: GeneralArea.Commander })
            .then(() => {
                dispatch(redirectToSignIn({ addRedirectBack: false }));
            })
            .catch(() => {
                dispatch(hideAppLoader());
            });
    };

export const redirectToSignIn =
    ({ addRedirectBack } = { addRedirectBack: true }): CommanderThunkAction =>
    (dispatch, _, { resolveRoute }) => {
        const params = addRedirectBack
            ? {
                  query: {
                      redirect: `${window.location.pathname}${window.location.search}${window.location.hash}`,
                  },
              }
            : undefined;
        const url = resolveRoute(RouteName.HomeSignIn, params);
        dispatch(linkRedirect(url));
    };

const getApplicationNormalizedUrl = createSelector(
    [getRouterBasePathRegex, getRouterBaseUrl, getServerBasePath, getStringProp('url')],
    (routerBasePathRegex, routerBaseUrl, serverBasePath, url) => {
        let normalizedUrl = url;

        /**
         * If the url does not match the application format, it's a server url and we need to transform it
         */
        if (!routerBasePathRegex.test(normalizedUrl)) {
            normalizedUrl = `${routerBaseUrl}${normalizedUrl.substring(serverBasePath.length)}`;
        }

        return normalizedUrl;
    }
);
